
import { createContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { logger } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast";

// Create a type that extends Session to include potential error property
type SessionWithError = Session & {
  error?: string;
};

export const SessionContext = createContext<{
  session: SessionWithError | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
}>({
  session: null,
  loading: true,
  refreshSession: async () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<SessionWithError | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Set Sentry user context from session
  const setSentryUser = (userSession: Session | null) => {
    if (userSession?.user) {
      Sentry.setUser({
        id: userSession.user.id,
        email: userSession.user.email || undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  };

  // Function to refresh session - can be called when session errors occur
  const refreshSession = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error({
          message: "Failed to refresh session",
          error: new Error(error.message),
        });
        
        // Only show toast for client-visible errors
        if (!error.message.includes("network error")) {
          toast({
            title: "Session error",
            description: "Please sign in again",
            variant: "destructive",
          });
        }
        
        setSession(null);
        setSentryUser(null);
      } else if (data.session) {
        setSession(data.session);
        setSentryUser(data.session);
      }
    } catch (error) {
      console.error("Session refresh error:", error);
      setSession(null);
      setSentryUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Initialize session and set up auth state listener
  useEffect(() => {
    // Initialize Supabase auth
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Set up auth state listener FIRST (to catch any auth events during initialization)
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((event, currentSession) => {
          logger.debug({
            message: "Auth state changed",
            context: { event, user: currentSession?.user?.email }
          });
          
          if (event === 'USER_UPDATED' && currentSession?.user) {
            logger.info({
              message: "User profile updated",
              context: { userId: currentSession.user.id }
            });
          }

          if (event === 'SIGNED_OUT') {
            setSentryUser(null);
          } else if (currentSession) {
            setSentryUser(currentSession);
          }
          
          setSession(currentSession);
        });
        
        // THEN check for existing session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error({
            message: "Error getting session",
            error: new Error(error.message),
          });
          
          const sessionWithError = data.session ? { ...data.session, error: error.message } : null;
          setSession(sessionWithError);
        } else {
          setSession(data.session);
        }
        
        if (data.session) {
          setSentryUser(data.session);
        }
        
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        logger.error({
          message: "Session retrieval error",
          error: error instanceof Error ? error : new Error(String(error)),
        });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  return (
    <SessionContext.Provider value={{ session, loading, refreshSession }}>
      {children}
    </SessionContext.Provider>
  );
};

export default AuthProvider;
